import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { DaysWithAvailability, getDaysWithAvailability } from "~/api/providers";
import { CalendarDay } from "~/shared/types";
import { INSURANCE_PROVIDERS, US_STATES } from "~/shared/utils/constants";
import { today } from "~/shared/utils/dates";
import { useSignUpStore } from "~/stores";

interface UseCalendarDaysReturn {
  calendarDays: CalendarDay[];
  currentStartDate: Date;
  navigatePrevWeek: () => void;
  navigateNextWeek: () => void;
  navigateToDate: (date: Date) => void;
  canNavigatePrev: boolean;
  isLoading: boolean;
  error: string | null;
  availabilityForNextMonth?: boolean;
  isMobile: boolean;
  firstAvailableDay: Date | null;
  isNavigating: boolean; // NEW: indicates week transition in progress
}

const getWeekDays = (startDate: Date): CalendarDay[] => {
  const days: CalendarDay[] = [];
  for (let i = 0; i < 7; i++) {
    const date = new Date(startDate);
    date.setDate(date.getDate() + i);
    days.push({ date: new Date(date), available: false });
  }
  return days;
};

const formatDate = (date: Date): string => date.toISOString().split("T")[0];

const getWeekStartDate = (targetDate: Date): Date => {
  const startDate = new Date(targetDate);
  const dayOfWeek = startDate.getDay();
  startDate.setDate(startDate.getDate() - dayOfWeek);
  return startDate;
};

// Helper to compare dates (ignoring time)
const isSameDay = (date1: Date, date2: Date): boolean => {
  return formatDate(date1) === formatDate(date2);
};

export const useCalendarDays = (
  selectedSpecialtiesProp: string[],
  selectedLocation: string | null,
  onFirstAvailableDayFound?: (date: Date | null) => void,
  // Waives the backend's minimum-notice buffer for the day counts. The admin
  // booking screen sets it so the badges agree with the slot list its provider
  // query renders — the two must move together or a day shows a count the list
  // below it cannot produce.
  staffBooking = false,
): UseCalendarDaysReturn => {
  // Guard against a caller passing a fresh array reference each render (e.g. an
  // inline literal). Key on the specialties' contents, not identity, so the
  // window-reset effect below doesn't refire every render — which, combined with
  // re-anchoring, would spin into an update loop.
  const specialtiesKey = JSON.stringify(selectedSpecialtiesProp);
  const selectedSpecialties = useMemo(
    () => selectedSpecialtiesProp,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [specialtiesKey],
  );

  const [targetStartDate, setTargetStartDate] = useState<Date>(today);
  const [displayedStartDate, setDisplayedStartDate] = useState<Date>(today);
  const [calendarDays, setCalendarDays] = useState<CalendarDay[]>(
    getWeekDays(today),
  );
  const [isMobile, setIsMobile] = useState(false);
  const [firstAvailableDay, setFirstAvailableDay] = useState<Date | null>(null);
  const [isAutoNavigating, setIsAutoNavigating] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const prevFirstAvailableDayRef = useRef<Date | null>(null);
  const autoNavAttemptsRef = useRef(0);
  // Must cover the backend's cached availability horizon (LOOKAHEAD_DAYS = 60) at the
  // smallest step, PLUS the re-anchor step, which draws from this same counter.
  // Each fetch scans a 7-day window (getWeekDays) and mobile advances 3 days/attempt, so
  // N steps reach day 3N + 6: covering day 60 needs 3N + 6 >= 60, i.e. 18 steps. A window
  // found out there still needs one re-anchor to bring the available day left-most, since
  // mobile renders only the first 3 of the 7 scanned days — and a budget spent entirely on
  // forward steps would skip it, painting an apparently empty calendar over a window that
  // does have availability. 22 leaves headroom for both. Desktop (7 days/attempt) reaches
  // the edge well within this.
  const MAX_AUTO_NAV_ATTEMPTS = 22;

  const { patient } = useSignUpStore();

  // Track if we're in a navigation transition (target differs from displayed)
  const isNavigating = useMemo(() => {
    return !isSameDay(targetStartDate, displayedStartDate);
  }, [targetStartDate, displayedStartDate]);

  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 640);
    };

    checkIfMobile();
    window.addEventListener("resize", checkIfMobile);
    return () => window.removeEventListener("resize", checkIfMobile);
  }, []);

  // Reset when filters change
  useEffect(() => {
    setTargetStartDate(today);
    setDisplayedStartDate(today);
    setCalendarDays(getWeekDays(today));
    prevFirstAvailableDayRef.current = null;
    setIsAutoNavigating(false);
    setIsInitialLoad(true);
    setFirstAvailableDay(null);
    autoNavAttemptsRef.current = 0;
  }, [selectedSpecialties, selectedLocation, patient.referringPracticeId]);

  const {
    data: availableDays,
    isLoading,
    error: queryError,
    isFetching,
  } = useQuery({
    queryKey: [
      "daysWithAvailability",
      selectedSpecialties,
      selectedLocation,
      formatDate(targetStartDate),
      staffBooking,
      patient.referringPracticeId ?? null,
    ],
    queryFn: async () => {
      const startDate = new Date(
        Math.max(targetStartDate.getTime(), today.getTime()),
      );
      const endDate = new Date(startDate);
      endDate.setDate(startDate.getDate() + 6);

      if (selectedSpecialties.length === 0) {
        return {} as DaysWithAvailability;
      }

      return getDaysWithAvailability({
        location: selectedLocation,
        startDate: formatDate(startDate),
        endDate: formatDate(endDate),
        state: US_STATES.find((state) => state.label === patient.state)?.value,
        primaryInsurance:
          INSURANCE_PROVIDERS[
            patient.primaryInsurance
              .providerName as keyof typeof INSURANCE_PROVIDERS
          ],
        secondaryInsurance: patient.secondaryInsurance?.providerName
          ? INSURANCE_PROVIDERS[
              patient.secondaryInsurance
                .providerName as keyof typeof INSURANCE_PROVIDERS
            ]
          : undefined,
        patientEmail: patient.email,
        patientId: patient.id,
        conditions: selectedSpecialties,
        staffBooking,
        referringPracticeId: patient.referringPracticeId,
      });
    },
    enabled: !!selectedLocation && selectedSpecialties.length > 0,
    // Keep previous data while fetching to prevent flicker
    placeholderData: (previousData) => previousData,
  });

  // Update calendar days only when we have fresh data for the target date
  useEffect(() => {
    // Don't update if still fetching data for a new target
    if (isFetching && isNavigating) {
      return;
    }

    // Only update display when we have data matching the target
    if (!availableDays || selectedSpecialties.length === 0) {
      return;
    }

    const days = getWeekDays(targetStartDate);
    days.forEach((day) => {
      day.available = (availableDays.dates?.[formatDate(day.date)] || 0) > 0;
    });

    const firstAvailableIndex = days.findIndex((day) => day.available);
    const firstAvailable =
      firstAvailableIndex >= 0 ? days[firstAvailableIndex] : undefined;

    // Re-anchor the window so the first available day is always left-most.
    // The scan window is 7 days but mobile renders only the first 3, so an
    // available day past index 2 would otherwise sit off-screen and violate
    // the "first available is left-most" requirement on both breakpoints.
    // Only re-anchor during the initial/auto search — never after the user has
    // manually navigated — and bound it with the same attempt counter as
    // auto-nav to rule out any anchor -> fetch -> anchor oscillation.
    if (
      firstAvailable &&
      firstAvailableIndex > 0 &&
      (isInitialLoad || isAutoNavigating) &&
      autoNavAttemptsRef.current < MAX_AUTO_NAV_ATTEMPTS &&
      !isSameDay(targetStartDate, firstAvailable.date)
    ) {
      autoNavAttemptsRef.current += 1;
      setIsAutoNavigating(true);
      setTargetStartDate(new Date(firstAvailable.date));
      return; // wait for the re-anchored window before painting
    }

    setCalendarDays(days);
    setDisplayedStartDate(targetStartDate);

    // Find first available day
    const newFirstAvailableDay = firstAvailable ? firstAvailable.date : null;

    // Only trigger callback if the first available day actually changed
    const prevDate = prevFirstAvailableDayRef.current;
    const hasChanged =
      (!prevDate && newFirstAvailableDay) ||
      (prevDate && !newFirstAvailableDay) ||
      (prevDate &&
        newFirstAvailableDay &&
        !isSameDay(prevDate, newFirstAvailableDay));

    if (hasChanged) {
      setFirstAvailableDay(newFirstAvailableDay);
      prevFirstAvailableDayRef.current = newFirstAvailableDay;
      onFirstAvailableDayFound?.(newFirstAvailableDay);
    }

    // Auto-navigate to find availability
    if (
      !firstAvailable &&
      selectedSpecialties.length > 0 &&
      (isInitialLoad || isAutoNavigating) &&
      availableDays?.availabilityForNextMonth === true &&
      autoNavAttemptsRef.current < MAX_AUTO_NAV_ATTEMPTS
    ) {
      setIsAutoNavigating(true);
      autoNavAttemptsRef.current += 1;

      const nextWeekStart = new Date(targetStartDate);
      const daysToMove = isMobile ? 3 : 7;
      nextWeekStart.setDate(nextWeekStart.getDate() + daysToMove);
      setTargetStartDate(nextWeekStart);
    } else if (firstAvailable) {
      // Found availability, stop auto-navigating
      setIsAutoNavigating(false);
      setIsInitialLoad(false);
      autoNavAttemptsRef.current = 0;
    } else if (
      availableDays?.availabilityForNextMonth === false ||
      autoNavAttemptsRef.current >= MAX_AUTO_NAV_ATTEMPTS
    ) {
      // No more availability or hit max attempts
      setIsAutoNavigating(false);
      setIsInitialLoad(false);
    }
  }, [
    targetStartDate,
    selectedSpecialties,
    selectedLocation,
    availableDays,
    isAutoNavigating,
    isFetching,
    isNavigating,
    isMobile,
    isInitialLoad,
    onFirstAvailableDayFound,
  ]);

  const navigateWeek = useCallback(
    (direction: "prev" | "next") => {
      setTargetStartDate((current) => {
        const newStartDate = new Date(current);
        const daysToMove = isMobile ? 3 : 7;
        newStartDate.setDate(
          newStartDate.getDate() +
            (direction === "prev" ? -daysToMove : daysToMove),
        );

        // Don't navigate before today
        if (direction === "prev" && newStartDate.getTime() < today.getTime()) {
          return current;
        }

        return newStartDate;
      });

      // Stop any auto-navigation when user manually navigates
      setIsAutoNavigating(false);
      setIsInitialLoad(false);
      autoNavAttemptsRef.current = 0;
    },
    [isMobile],
  );

  const navigateToDate = useCallback((targetDate: Date) => {
    const weekStartDate = getWeekStartDate(targetDate);
    setTargetStartDate(weekStartDate);
    setIsAutoNavigating(false);
    setIsInitialLoad(false);
    autoNavAttemptsRef.current = 0;
  }, []);

  const canNavigatePrev = useMemo(() => {
    return targetStartDate.getTime() > today.getTime();
  }, [targetStartDate]);

  return {
    calendarDays,
    currentStartDate: displayedStartDate,
    navigatePrevWeek: () => navigateWeek("prev"),
    navigateNextWeek: () => navigateWeek("next"),
    navigateToDate,
    canNavigatePrev,
    isLoading: isLoading || (isFetching && isNavigating) || isAutoNavigating,
    error: queryError instanceof Error ? queryError.message : null,
    availabilityForNextMonth: availableDays?.availabilityForNextMonth,
    isMobile,
    firstAvailableDay,
    isNavigating,
  };
};
